import React from "react";
import { useTVDisplay } from "./hooks/useTVDisplay";
import TVClassCard from "./components/TVClassCard";
import new1Bg from "../../../assets/images/new1Bg.png";
import neoLogo from "../../../assets/images/Neo_Logo_A.svg";
import "moment/locale/pt-br";
import { Spinner } from "reactstrap";
import { Link } from "react-router-dom";

const TVDisplay = () => {
    document.title = "TV Display | NeoApp";

    const { currentSessions, currentTime, loading, tenantLogo } = useTVDisplay();
    const [bgLoaded, setBgLoaded] = React.useState(false);

    React.useEffect(() => {
        const img = new Image();
        img.src = new1Bg;
        img.onload = () => {
            setBgLoaded(true);
        };
        img.onerror = () => {
            setBgLoaded(true); // Evita travamento infinito
        };
    }, []);

    // Loader unificado do Kiosk para garantir que não pisque a imagem
    if (!bgLoaded || loading) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center vh-100" style={{ backgroundColor: "#f8f8fb" }}>
                <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                <h5 className="mt-3" style={{ color: "#495057", fontWeight: "500" }}>Carregando Tela...</h5>
            </div>
        );
    }

    // Calcula de forma inteligente colunas para sempre caber na tela sem rolagem
    const numCards = currentSessions.length;
    let gridCols = 1;
    if (numCards > 0) {
        if (numCards <= 2) { gridCols = numCards; }
        else if (numCards <= 4) { gridCols = 2; }
        else if (numCards <= 6) { gridCols = 3; }
        else if (numCards <= 8) { gridCols = 4; } // Telas wide suportam bem 4 colunas
        else if (numCards <= 9) { gridCols = 3; }
        else { gridCols = 4; }
    }

    return (
        <div
            className="d-flex flex-column vh-100 vw-100 overflow-hidden"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                backgroundColor: "#2a3042",
                backgroundImage: `url(${new1Bg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: 9999
            }}
        >
            {/* Overlay Glassmorphism */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    backdropFilter: "blur(15px)",
                    WebkitBackdropFilter: "blur(15px)",
                    zIndex: 0
                }}
            />

            {/* Conteúdo Principal (z-index: 1 para ficar acima do overlay) */}
            <div className="d-flex flex-column h-100" style={{ zIndex: 1, position: "relative" }}>

                {/* ═══ Header Premium - Faixa Top ═══ */}
                <div
                    className="d-flex justify-content-between align-items-center position-sticky top-0 w-100"
                    style={{
                        zIndex: 50,
                        boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                        padding: "3vh 4vw" // Margem segura para TV (Overscan)
                    }}
                >
                    <div className="d-flex align-items-center">
                        {tenantLogo && (
                            <img
                                src={tenantLogo}
                                alt="Logo Academia"
                                style={{ maxHeight: "50px", maxWidth: "180px", objectFit: "contain", filter: "brightness(0) invert(1)" }}
                            />
                        )}
                        <div className="ms-4 border-start border-light border-opacity-25 ps-4">
                            <h2 className="mb-0 fw-bold text-white fs-2" style={{ letterSpacing: "1px" }}>
                                Aulas em Andamento
                            </h2>
                            <p className="mb-0 text-white-50 fs-5">Bem-vindo(a) ao seu treino</p>
                        </div>
                    </div>

                    {/* Digital Clock */}
                    <div className="text-end">
                        <h1 className="fw-bolder mb-0 text-white" style={{ fontSize: "3rem", letterSpacing: "2px" }}>
                            {currentTime.format("HH:mm")}
                        </h1>
                        <p className="text-white-50 fw-medium fs-5 mb-0 text-uppercase" style={{ letterSpacing: "1px" }}>
                            {currentTime.locale("pt-BR").format("dddd, D [de] MMMM")}
                        </p>
                    </div>
                </div>

                {/* ═══ Body / Grid de Componentes ═══ */}
                <div className="flex-grow-1 overflow-hidden d-flex flex-column justify-content-start" style={{ padding: "4vh 4vw 6vh 4vw" }}>

                    {currentSessions.length > 0 ? (
                        <div
                            className="w-100"
                            style={{
                                display: "grid",
                                gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                                gridAutoRows: "1fr", // Mantém altura igual para todas as linhas
                                gap: "24px",
                                alignItems: "stretch"
                            }}
                        >
                            {currentSessions.map((session, index) => (
                                <TVClassCard key={session.id || index} session={session} />
                            ))}
                        </div>
                    ) : (
                        /* ESTADO VAZIO: Nenhuma aula agora */
                        <div className="d-flex flex-column align-items-center justify-content-center text-center user-select-none opacity-75" style={{ flex: 1 }}>
                            <h1 className="fw-bolder mt-3 text-white mb-1" style={{ fontSize: "3rem" }}>Sem aulas agendadas</h1>
                            <p className="font-size-24 text-white-50 fw-medium">Nado livre disponível.</p>
                        </div>
                    )}
                </div>

                {/* Powered By (Canto Inferior Direito com Margem Segura) */}
                <div className="position-absolute" style={{ bottom: '4vh', right: '4vw', zIndex: 10 }}>
                    <Link to="/dashboard" className="text-decoration-none">
                        <div className="d-flex align-items-center gap-2 opacity-50" style={{ cursor: "pointer", transition: "all 0.2s ease" }} onMouseEnter={(e) => e.currentTarget.style.opacity = "1"} onMouseLeave={(e) => e.currentTarget.style.opacity = "0.5"}>
                            <span style={{ fontSize: "12px", fontWeight: "700", color: "#ffffff", letterSpacing: "1.5px" }}>POWERED BY</span>
                            <img src={neoLogo} alt="NeoApp" style={{ height: "16px", filter: "brightness(0) invert(1)" }} />
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default TVDisplay;
